import React, { useEffect, useRef, useState } from "react";

const DEFAULT_AVATARS = [
  { id: 1, bg: "bg-slate-400" },
  { id: 2, bg: "bg-slate-500" },
  { id: 3, bg: "bg-slate-600" },
];

/**
 * Universal hero section. Renders only blocks for which data is provided.
 * Order: title → subheading → intro (first para) → image → credibility/stats → bottomParagraph (second para).
 * - title: required
 * - subheading, intro, image, imageAlt, credibility, bottomParagraph: optional; omitted sections are not rendered.
 */
/** Positions for floating stat cards on the image (so they sit on the edge) */
const FLOATING_CARD_POSITIONS = [
  "left-4 bottom-6",
  "right-5 top-5",
  "right-6 bottom-8",
];

export default function Hero({
  title,
  subheading,
  intro,
  image,
  video,
  imageAlt = "",
  credibility,
  bottomParagraph,
  statsAsFloatingCards = false,
  useBrandBackground = true,
}) {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  const imageSrc =
    image != null
      ? typeof image === "string"
        ? image
        : image?.default ?? image
      : "";
  const videoSrc =
    video != null
      ? typeof video === "string"
        ? video
        : video?.default ?? video
      : "";
  const hasImage = typeof imageSrc === "string" && imageSrc.length > 0;
  const hasVideo = typeof videoSrc === "string" && videoSrc.length > 0;
  const hasVisual = hasVideo || hasImage;
  const hasStatsAsCards =
    statsAsFloatingCards &&
    credibility?.stats &&
    credibility.stats.length > 0;
  const hasCredibility =
    credibility &&
    !hasStatsAsCards &&
    (credibility.ratingText ||
      (credibility.stats && credibility.stats.length > 0));
  const hasBottom = bottomParagraph && typeof bottomParagraph === "string";
  const isMinimalHero = !intro && !hasVisual && !hasCredibility && !hasBottom;

  useEffect(() => {
    if (!hasVideo || !videoRef.current) return;

    const tryAutoplay = async () => {
      try {
        await videoRef.current.play();
      } catch {
        // Browsers often block autoplay with sound; fallback to muted autoplay.
        setIsMuted(true);
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {});
      }
    };

    tryAutoplay();
  }, [hasVideo]);

  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = isMuted;
  }, [isMuted]);

  return (
    <section
      className={`relative text-gray-900 ${
        useBrandBackground ? "detail-hero-bg" : "bg-[#f8f8f4]"
      }`}
    >
      <div className="container-app mx-auto max-w-4xl px-4 py-16 md:py-20">
        {title && (
          <h1 className={`text-4xl font-medium tracking-normal text-gray-900 sm:text-5xl md:text-6xl ${isMinimalHero ? "text-center" : "text-left"}`}>
            {title}
          </h1>
        )}

        {subheading && (
          <p className={`mt-3 text-xl font-semibold leading-snug text-gray-700 md:text-xl ${isMinimalHero ? "text-center" : "text-left"}`}>
            {subheading}
          </p>
        )}

        {intro && (
          <p className="mt-6 text-left text-base leading-relaxed text-gray-700 md:text-lg">
            {intro}
          </p>
        )}

        {hasVisual && (
          <>
            <div className="mt-12 w-full overflow-hidden rounded-2xl relative">
              {hasVideo ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoSrc}
                    autoPlay
                    muted={isMuted}
                    loop
                    playsInline
                    preload="metadata"
                    className="h-[300px] sm:h-[440px] md:h-[600px] w-full object-cover object-center"
                  />
                </>
              ) : (
                <img
                  src={imageSrc}
                  alt={imageAlt || (title ? `Hero-Bild: ${title}` : "Hero-Bild")}
                  className="h-[300px] sm:h-[440px] md:h-[600px] w-full object-cover object-center"
                />
              )}
              {/* Floating stat cards: only on tablet/desktop so they don't crowd the image on mobile */}
              {hasStatsAsCards && (
                <div className="hidden sm:block">
                  {credibility.stats.map((stat, i) => (
                    <div
                      key={i}
                      className={`absolute ${
                        FLOATING_CARD_POSITIONS[i] ?? "bottom-4 left-4"
                      } w-[min(180px,42vw)] rounded-xl border border-white/90 bg-white/95 p-3 shadow-[0_8px_32px_rgba(0,0,0,0.12)] backdrop-blur-sm transition-transform duration-200 hover:scale-105`}
                    >
                      <div className="text-lg font-semibold tracking-tight text-gray-900 md:text-xl">
                        {stat.value}
                      </div>
                      <div className="mt-0.5 text-xs font-medium text-gray-600 md:text-sm">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Compact stat cards: mobile-only under the image so both image and cards are readable */}
            {hasStatsAsCards && (
              <div className="mt-6 grid gap-3 sm:hidden">
                {credibility.stats.map((stat, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-xl border border-white/90 bg-white/95 px-4 py-3 shadow-[0_6px_18px_rgba(0,0,0,0.10)]"
                  >
                    <div className="text-base font-semibold tracking-tight text-gray-900">
                      {stat.value}
                    </div>
                    <div className="ml-3 text-xs font-medium text-gray-600 text-right">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {hasCredibility && (
          <div className="mt-10 flex flex-wrap items-center gap-8">
            {credibility.ratingText && (
              <div className="flex items-center gap-3">
                {credibility.showAvatars !== false && (
                  <div className="flex -space-x-2">
                    {(credibility.avatarPlaceholders || DEFAULT_AVATARS).map(
                      (a) => (
                        <div
                          key={a.id}
                          className={`h-9 w-9 shrink-0 rounded-full border-2 border-[#f8f8f4] ${a.bg}`}
                          aria-hidden
                        />
                      )
                    )}
                  </div>
                )}
                <p className="text-sm font-medium text-gray-700">
                  {credibility.ratingText}
                </p>
              </div>
            )}
            {credibility.stats && credibility.stats.length > 0 && (
              <div className="flex flex-wrap items-center gap-6 text-sm font-medium text-gray-700">
                {credibility.stats.map((stat, i) => (
                  <span key={i}>
                    {stat.value && (
                      <strong className="text-gray-900">{stat.value} </strong>
                    )}
                    {stat.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {hasBottom && (
          <p className="mt-10 max-w-full text-left text-sm leading-relaxed text-gray-700 md:text-base">
            {bottomParagraph}
          </p>
        )}
      </div>
    </section>
  );
}
